import Phaser from 'phaser';
import { MoleManager } from '../MoleManager';
import { Hat } from '../Hat';

export class GameScene extends Phaser.Scene {
    private holes: Phaser.GameObjects.Ellipse[] = [];
    private holePositions: { x: number; y: number }[] = [];
    private moleManager!: MoleManager;
    private hat!: Hat;
    private scoreText!: Phaser.GameObjects.Text;
    private blueMolesText!: Phaser.GameObjects.Text;
    private redMolesText!: Phaser.GameObjects.Text;
    private winLoseOverlay?: Phaser.GameObjects.Container;
    private backgroundMusic?: Phaser.Sound.BaseSound;
    
    // Swipe detection
    private swipeStartX = 0;
    private swipeStartY = 0;
    private swipeStartTime = 0;
    private isSwiping = false;

    constructor() {
        super({ key: 'GameScene' });
    }

    preload(): void {
        // Load only essential images first - game will start immediately after these load
        // Load stand image
        this.load.image('moleStand', '/art/molestand.png');
        // Load reference image with blue dots marking hole centers
        this.load.image('moleStandAlign', '/art/molestandALIGN.png');
        
        // Load flying sombrero images
        this.load.image('flyingSombrero01', '/art/flyingsombrero01.png');
        this.load.image('flyingSombrero02', '/art/flyingsombrero02.png');
        
        // Mole images will be loaded in the background after game starts
    }
    
    private loadMoleImagesInBackground(): void {
        // Load mole images in the background - game can continue while these load
        // Load mole images for BlueMole01
        this.load.image('AOCMole', '/art/AOCMole.png');
        this.load.image('AOCSombrero', '/art/AOCSombrero.png');
        
        // Load mole images for BlueMole02
        this.load.image('hakeemMole', '/art/hakeemmole.png');
        this.load.image('hakeemSombrero', '/art/hakeemsombrero.png');
        
        // Load mole images for BlueMole03
        this.load.image('kamalaMole', '/art/kamalamole.png');
        this.load.image('kamalaSombrero', '/art/kamalasombrero.png');
        
        // Load mole images for BlueMole04
        this.load.image('pelosiMole', '/art/pelosimole.png');
        this.load.image('pelosiSombrero', '/art/pelosisombrero.png');
        
        // Load mole images for BlueMole05
        this.load.image('chuckMole', '/art/chuckmole.png');
        this.load.image('chuckSombrero', '/art/chucksombrero.png');
        
        // Load mole images for BlueMole06
        this.load.image('warrenMole', '/art/warrenmole.png');
        this.load.image('warrenSombrero', '/art/warrensombrero.png');
        
        // Load mole images for RedMole01
        this.load.image('bondiMole', '/art/bondimole.png');
        this.load.image('bondiSombrero', '/art/bondisombrero.png');
        
        // Load mole images for RedMole02
        this.load.image('kashMole', '/art/kashmole.png');
        this.load.image('kashSombrero', '/art/kashsombrero.png');
        
        // Load mole images for RedMole03
        this.load.image('melaniaMole', '/art/melaniamole.png');
        this.load.image('melaniaSombrero', '/art/melaniasombrero.png');
        
        // Load mole images for RedMole04
        this.load.image('rfkMole', '/art/rfkmole.png');
        this.load.image('rfkSombrero', '/art/rfksombrero.png');
        
        // Load mole images for RedMole05
        this.load.image('trumpMole', '/art/trumpmole.png');
        this.load.image('trumpSombrero', '/art/trumpsombrero.png');
        
        // Load mole images for RedMole06
        this.load.image('vanceMole', '/art/vancemole.png');
        this.load.image('vanceSombrero', '/art/fatvancesombrero.png');
        
        // Load sound for Trump mole freeze
        this.load.audio('muymalo', '/art/muymalo.mp3');
        
        // Load sound for game over (when red moles are frozen)
        this.load.audio('gameover', '/art/gameover.mp3');
        
        // Load sound for blue mole freeze
        this.load.audio('hitdem', '/art/hitdem.mp3');
        
        // Load sound for red mole freeze
        this.load.audio('hitgop', '/art/hitgop.mp3');
        
        // Load sound for hat throw
        this.load.audio('throw', '/art/throw.mp3');
        
        // Load background music
        this.load.audio('sombrerosong', '/art/sombrerosong.mp3');
        
        // Load sound for winning with all blue moles frozen
        this.load.audio('mariachi', '/art/mariachi.mp3');
        
        // Start loading in background
        this.load.start();
        
        // Once images are loaded, trigger a mole respawn check and start music if not already playing
        this.load.once('complete', () => {
            // All mole images are now loaded, ensure moles can spawn
            if (this.moleManager) {
                // Force a check to spawn moles that were waiting for images
                this.time.delayedCall(100, () => {
                    // Moles will spawn naturally through the normal spawn cycle
                });
            }
            
            // Start background music if it's loaded and not already playing
            this.startBackgroundMusic();
        });
    }
    
    private startBackgroundMusic(): void {
        // Start background music if it's loaded and not already playing
        if (!this.backgroundMusic && this.cache.audio.exists('sombrerosong')) {
            try {
                // Stop any existing instance first
                const existingSound = this.sound.get('sombrerosong');
                if (existingSound && existingSound.isPlaying) {
                    existingSound.stop();
                }
                
                this.backgroundMusic = this.sound.add('sombrerosong', { loop: true, volume: 0.4 });
                this.backgroundMusic.play();
            } catch (e) {
                // Error starting music, ignore
            }
        } else if (this.backgroundMusic && !this.backgroundMusic.isPlaying) {
            // Music exists but not playing, restart it
            try {
                this.backgroundMusic.play();
            } catch (e) {
                // Error restarting music, ignore
            }
        }
    }

    shutdown(): void {
        // Clean up when scene is shut down (e.g., when going to menu)
        if (this.backgroundMusic && this.backgroundMusic.isPlaying) {
            this.backgroundMusic.stop();
        }
        this.backgroundMusic = undefined;
        
        // Clear all moles and reset state
        if (this.moleManager) {
            this.moleManager.clearAllMoles();
        }
        
        // Reset hat
        if (this.hat) {
            this.hat.resetHat();
        }
        
        // Clear overlay
        if (this.winLoseOverlay) {
            this.winLoseOverlay.destroy();
            this.winLoseOverlay = undefined;
        }
    }

    create(): void {
        // The ad banner is managed outside the modal in index.html
        // We don't create or show ads here since they should be hidden when modal is open
        // The ad container outside the modal will be shown/hidden by the modal JavaScript
        
        // Reset background music reference when scene is created/restarted
        this.backgroundMusic = undefined;
        
        // Ensure all state is reset
        this.winLoseOverlay = undefined;
        this.holes = [];
        this.holePositions = [];
        
        // Game dimensions
        const gameWidth = 360;
        const gameHeight = 640;
        
        // TEMPORARY: Coordinate picker mode - set to true, click on blue dots, copy coordinates from console
        const showReference = false; // Set to true to see reference image and pick coordinates
        
        if (showReference) {
            // Show reference image with blue dots
            this.add.image(gameWidth / 2, gameHeight / 2, 'moleStandAlign')
                .setOrigin(0.5, 0.5)
                .setDepth(0);
            
            // Add click handler to get coordinates
            this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
                const x = Math.round(pointer.x);
                const y = Math.round(pointer.y);
                console.log(`Hole position: { x: ${x}, y: ${y} },`);
                // Show coordinate text at click location
                this.add.text(x, y, `(${x},${y})`, {
                    fontSize: '14px',
                    color: '#ffff00',
                    backgroundColor: '#000000',
                    padding: { x: 4, y: 2 }
                }).setDepth(200);
            });
            
            // Instructions
            this.add.text(gameWidth / 2, 50, 'Click on each blue dot to get coordinates', {
                fontSize: '16px',
                color: '#ffffff',
                backgroundColor: '#000000',
                padding: { x: 10, y: 5 }
            }).setOrigin(0.5).setDepth(200);
            return; // Exit early when in reference mode
        }
        
        // Try to automatically detect blue dots from the reference image
        // Delay this to allow game to render first (non-blocking)
        this.time.delayedCall(100, () => {
            this.detectBlueDotPositions();
        });
        
        // Display the stand image as background (covers entire canvas)
        this.add.image(gameWidth / 2, gameHeight / 2, 'moleStand')
            .setOrigin(0.5, 0.5)
            .setDepth(0);
        
        // Set background color (will show if stand image has transparency)
        this.cameras.main.setBackgroundColor('#87CEEB'); // Sky blue
        
        // Position holes to match the stand image
        // Coordinates will be auto-detected from molestandALIGN.png blue dots
        // Lower tier holes (front row, 3 holes) - from left to right
        let lowerHoles = [
            { x: 80, y: 400 },   // Bottom left - will be auto-updated
            { x: 180, y: 400 },  // Bottom center - will be auto-updated
            { x: 280, y: 400 }   // Bottom right - will be auto-updated
        ];
        
        // Upper tier holes (back row, 3 holes) - from left to right
        let upperHoles = [
            { x: 90, y: 180 },   // Top left - will be auto-updated
            { x: 180, y: 180 },  // Top center - will be auto-updated
            { x: 270, y: 180 }   // Top right - will be auto-updated
        ];
        
        // Check if we have detected positions from the reference image
        if ((window as any).detectedHolePositions) {
            const detected = (window as any).detectedHolePositions;
            if (detected.lower && detected.lower.length === 3) {
                lowerHoles = detected.lower;
            }
            if (detected.upper && detected.upper.length === 3) {
                upperHoles = detected.upper;
            }
        }
        
        const holeRadius = 35;
        
        // Create lower tier holes (3 holes) - with visible markers for alignment
        for (let i = 0; i < 3; i++) {
            const pos = lowerHoles[i];
            this.holePositions.push({ x: pos.x, y: pos.y });
            
            // Create invisible hole for positioning (stand image has the visual holes)
            const hole = this.add.ellipse(pos.x, pos.y, holeRadius * 2, holeRadius * 1.2, 0x1a1a1a, 0)
                .setDepth(10)
                .setVisible(false); // Hide since stand image has them
            
            this.holes.push(hole);
        }
        
        // Create upper tier holes (3 holes) - invisible, just for positioning
        for (let i = 0; i < 3; i++) {
            const pos = upperHoles[i];
            this.holePositions.push({ x: pos.x, y: pos.y });
            
            // Create invisible hole for positioning (stand image has the visual holes)
            const hole = this.add.ellipse(pos.x, pos.y, holeRadius * 2, holeRadius * 1.2, 0x1a1a1a, 0)
                .setDepth(10)
                .setVisible(false); // Hide since stand image has them
            
            this.holes.push(hole);
        }

        // Create UI elements
        this.createUI();

        // Initialize MoleManager (will be reset in startGame, but ensure clean state)
        this.moleManager = new MoleManager(this, this.holes, this.holePositions);
        // Reset MoleManager to ensure clean state
        this.moleManager.reset();

        // Initialize Hat
        this.hat = new Hat(this);

        // Set up swipe detection
        this.setupSwipeDetection();

        // Start the game
        this.moleManager.startGame();
        
        // Try to start music immediately if already loaded (cached)
        this.startBackgroundMusic();
        
        // Load mole images in the background (non-blocking)
        // Music will also start when loading completes (handled in loadMoleImagesInBackground)
        this.loadMoleImagesInBackground();
    }
    
    private detectBlueDotPositions(): void {
        // Try to read the reference image texture and find blue pixels
        if (!this.textures.exists('moleStandAlign')) {
            // Texture not loaded yet, try again after a delay
            this.time.delayedCall(500, () => this.detectBlueDotPositions());
            return;
        }
        
        const texture = this.textures.get('moleStandAlign');
        const image = texture.getSourceImage() as HTMLImageElement;
        
        if (!image || !image.complete) {
            // Image not loaded yet, try again after a delay
            this.time.delayedCall(500, () => this.detectBlueDotPositions());
            return;
        }
        
        // Use setTimeout to make this non-blocking and allow the game to render first
        setTimeout(() => {
            // Create a canvas to read pixel data
            const canvas = document.createElement('canvas');
            canvas.width = image.width;
            canvas.height = image.height;
            const ctx = canvas.getContext('2d');
            if (!ctx) return;
            
            ctx.drawImage(image, 0, 0);
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;
            
            // Find blue pixels (looking for bright blue: RGB values like 0, 0, 255 or similar)
            // Optimize: sample every 2 pixels to speed up detection (4x faster)
            const blueDots: { x: number; y: number; intensity: number }[] = [];
            const blueThreshold = 150; // Minimum blue value to consider it a blue dot
            const sampleStep = 2; // Sample every 2 pixels for 4x speed improvement
            
            for (let y = 0; y < image.height; y += sampleStep) {
                for (let x = 0; x < image.width; x += sampleStep) {
                    const idx = (y * image.width + x) * 4;
                    const r = data[idx];
                    const g = data[idx + 1];
                    const b = data[idx + 2];
                    
                    // Check if this is a blue pixel (high blue, low red/green)
                    if (b > blueThreshold && r < 150 && g < 150) {
                        blueDots.push({ x, y, intensity: b });
                    }
                }
            }
            
            // Group nearby blue pixels into clusters (each dot is multiple pixels)
            const clusters: { x: number; y: number; count: number }[] = [];
            const clusterRadius = 15;
            
            for (const dot of blueDots) {
                // Find existing cluster near this pixel
                let foundCluster = false;
                for (const cluster of clusters) {
                    const dist = Math.sqrt(Math.pow(cluster.x - dot.x, 2) + Math.pow(cluster.y - dot.y, 2));
                    if (dist < clusterRadius) {
                        // Add to existing cluster (weighted average)
                        cluster.x = (cluster.x * cluster.count + dot.x) / (cluster.count + 1);
                        cluster.y = (cluster.y * cluster.count + dot.y) / (cluster.count + 1);
                        cluster.count++;
                        foundCluster = true;
                        break;
                    }
                }
                
                if (!foundCluster) {
                    clusters.push({ x: dot.x, y: dot.y, count: 1 });
                }
            }
            
            // Sort clusters: lower Y values first (upper tier), then by X (left to right)
            clusters.sort((a, b) => {
                if (Math.abs(a.y - b.y) > 50) {
                    return a.y - b.y; // Different rows
                }
                return a.x - b.x; // Same row, sort by X
            });
            
            if (clusters.length >= 6) {
                console.log('=== DETECTED BLUE DOT POSITIONS ===');
                console.log('Lower tier (front row, bottom 3):');
                const lowerTier = clusters.slice(0, 3);
                for (let i = 0; i < lowerTier.length; i++) {
                    const hole = lowerTier[i];
                    console.log(`  { x: ${Math.round(hole.x)}, y: ${Math.round(hole.y)} }, // Bottom ${i === 0 ? 'left' : i === 1 ? 'center' : 'right'}`);
                }
                console.log('Upper tier (back row, top 3):');
                const upperTier = clusters.slice(3, 6);
                for (let i = 0; i < upperTier.length; i++) {
                    const hole = upperTier[i];
                    console.log(`  { x: ${Math.round(hole.x)}, y: ${Math.round(hole.y)} }, // Top ${i === 0 ? 'left' : i === 1 ? 'center' : 'right'}`);
                }
                console.log('===================================');
                
                // Auto-update the hole positions
                this.autoUpdateHolePositions(lowerTier, upperTier);
            } else {
                console.warn(`Found ${clusters.length} blue dot clusters, expected 6.`);
                console.log('Please use coordinate picker: Set showReference = true in GameScene.ts');
            }
        }, 100); // Delay 100ms to let the game render first
    }
    
    private autoUpdateHolePositions(lowerTier: { x: number; y: number }[], upperTier: { x: number; y: number }[]): void {
        // Store detected positions globally so they can be used in create()
        const detected = {
            lower: lowerTier.map(h => ({ x: Math.round(h.x), y: Math.round(h.y) })),
            upper: upperTier.map(h => ({ x: Math.round(h.x), y: Math.round(h.y) }))
        };
        
        (window as any).detectedHolePositions = detected;
        
        // Update the hole positions in the scene
        // Note: This runs after create(), so we need to update the existing positions
        if (this.holePositions.length >= 6) {
            // Update lower tier
            for (let i = 0; i < 3; i++) {
                this.holePositions[i].x = detected.lower[i].x;
                this.holePositions[i].y = detected.lower[i].y;
            }
            // Update upper tier
            for (let i = 0; i < 3; i++) {
                this.holePositions[i + 3].x = detected.upper[i].x;
                this.holePositions[i + 3].y = detected.upper[i].y;
            }
            
            // Update marker positions
            this.children.list.forEach((child: any) => {
                if (child.text && child.text.includes('L')) {
                    const match = child.text.match(/L(\d)/);
                    if (match && match[1]) {
                        const index = parseInt(match[1]) - 1;
                        if (index < 3) {
                            child.setPosition(detected.lower[index].x, detected.lower[index].y + 15);
                            child.setText(`L${index + 1}\n(${detected.lower[index].x},${detected.lower[index].y})`);
                        }
                    }
                }
                if (child.text && child.text.includes('U')) {
                    const match = child.text.match(/U(\d)/);
                    if (match && match[1]) {
                        const index = parseInt(match[1]) - 1;
                        if (index < 3) {
                            child.setPosition(detected.upper[index].x, detected.upper[index].y + 15);
                            child.setText(`U${index + 1}\n(${detected.upper[index].x},${detected.upper[index].y})`);
                        }
                    }
                }
                if (child.type === 'Circle' && (child.fillColor === 0x00ff00 || child.fillColor === 0xff00ff)) {
                    // Update circle positions to match detected positions
                }
            });
            
            console.log('Hole positions automatically updated!');
        }
    }

    private setupSwipeDetection(): void {
        // Make the entire scene interactive for swipe detection
        this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
            if (!this.moleManager.isGameActive() || this.hat.isHatThrown()) return;
            
            this.isSwiping = true;
            this.swipeStartX = pointer.x;
            this.swipeStartY = pointer.y;
            this.swipeStartTime = this.time.now;
            
            // Swipe line removed - no visual feedback needed
        });

        this.input.on('pointermove', (_pointer: Phaser.Input.Pointer) => {
            // No visual feedback needed during swipe
        });

        this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
            if (!this.isSwiping) return;
            
            this.isSwiping = false;
            
            // Calculate swipe - hat always spawns from bottom center
            const hatStartX = this.cameras.main.centerX;
            const hatStartY = this.cameras.main.height - 50;
            
            const swipeEndX = pointer.x;
            const swipeEndY = pointer.y;
            
            // Calculate direction from bottom center to swipe end
            const dx = swipeEndX - hatStartX;
            const dy = swipeEndY - hatStartY;
            const distance = Phaser.Math.Distance.Between(hatStartX, hatStartY, swipeEndX, swipeEndY);
            // Swipe time calculated but not used for constant speed
            
            // Minimum swipe distance to throw
            const minSwipeDistance = 30;
            
            if (distance >= minSwipeDistance) {
                // Constant fast speed - direction based on swipe, speed is always the same
                const constantSpeed = 600; // Fast constant speed
                
                // Calculate direction from swipe
                const velocityX = (dx / distance) * constantSpeed;
                const velocityY = (dy / distance) * constantSpeed;
                
                // Play sound when hat is thrown
                try {
                    if (this.cache.audio.exists('throw')) {
                        this.sound.play('throw');
                    }
                } catch (e) {
                    // Sound not loaded yet, ignore
                }
                
                // Throw hat from bottom center with constant speed
                this.hat.throwHat(hatStartX, hatStartY, velocityX, velocityY);
            }
            
        });
    }

    private createUI(): void {
        // Retro arcade font style
        const retroStyle = {
            fontSize: '20px',
            color: '#ffffff',
            fontFamily: 'Courier New, monospace',
            fontStyle: 'bold',
            backgroundColor: '#000000',
            padding: { x: 8, y: 4 },
            stroke: '#000000',
            strokeThickness: 0
        };
        
        // Score display (top left)
        this.scoreText = this.add.text(10, 10, 'SCORE: 10000', retroStyle).setDepth(100);

        // Total moles caught counter (top right)
        this.blueMolesText = this.add.text(
            this.cameras.main.width - 10,
            10,
            'CAUGHT: 0/6',
            { ...retroStyle, color: '#4169E1', align: 'right' }
        ).setOrigin(1, 0).setDepth(100);

        // DEM/GOP breakdown (below total)
        this.redMolesText = this.add.text(
            this.cameras.main.width - 10,
            40,
            'DEM: 0  GOP: 0',
            { ...retroStyle, color: '#FFFFFF', align: 'right', fontSize: '16px' }
        ).setOrigin(1, 0).setDepth(100);
    }
    
    private showWinLoseScreen(isWin: boolean): void {
        if (this.winLoseOverlay) return;
        
        // Stop background music when game ends
        if (this.backgroundMusic && this.backgroundMusic.isPlaying) {
            this.backgroundMusic.stop();
        }
        
        // Check if all 6 blue moles were frozen
        const blueMolesFrozen = this.moleManager.getBlueMolesFrozen();
        const redMolesCaught = this.moleManager.getRedMolesCaught();
        
        // Play mariachi sound if all 6 blue moles are frozen
        if (blueMolesFrozen === 6) {
            try {
                if (this.cache.audio.exists('mariachi')) {
                    this.sound.play('mariachi');
                }
            } catch (e) {
                // Sound not loaded yet, ignore
            }
        }
        
        // Play game over sound if any red moles were frozen
        if (redMolesCaught > 0) {
            try {
                if (this.cache.audio.exists('gameover')) {
                    this.sound.play('gameover');
                }
            } catch (e) {
                // Sound not loaded yet, ignore
            }
        }
        
        const centerX = this.cameras.main.centerX;
        const centerY = this.cameras.main.centerY;
        
        // Dark overlay
        const overlay = this.add.rectangle(centerX, centerY, 360, 640, 0x000000, 0.8).setDepth(200);
        
        // Title
        const titleText = isWin ? 'YOU WIN!' : 'GAME OVER!';
        const titleColor = isWin ? '#00ff00' : '#ff0000';
        const title = this.add.text(
            centerX,
            centerY - 100,
            titleText,
            {
                fontSize: '48px',
                color: titleColor,
                fontFamily: 'Courier New, monospace',
                fontStyle: 'bold',
                stroke: '#000000',
                strokeThickness: 4
            }
        ).setOrigin(0.5).setDepth(201);
        
        // Final score
        const finalScore = this.moleManager.calculateScore();
        const elapsedSeconds = (this.moleManager.getElapsedTime() / 1000).toFixed(1);
        
        // Save high score
        const currentHighScore = parseInt(localStorage.getItem('sombreroTossHighScore') || '0', 10);
        if (finalScore > currentHighScore) {
            localStorage.setItem('sombreroTossHighScore', Math.floor(finalScore).toString());
        }
        
        const scoreText = this.add.text(
            centerX,
            centerY - 20,
            `FINAL SCORE: ${Math.floor(finalScore)}`,
            {
                fontSize: '24px',
                color: '#ffffff',
                fontFamily: 'Courier New, monospace',
                fontStyle: 'bold'
            }
        ).setOrigin(0.5).setDepth(201);
        
        const timeText = this.add.text(
            centerX,
            centerY + 20,
            `TIME: ${elapsedSeconds}s`,
            {
                fontSize: '20px',
                color: '#ffffff',
                fontFamily: 'Courier New, monospace'
            }
        ).setOrigin(0.5).setDepth(201);
        
        // Close button
        const buttonBg = this.add.rectangle(centerX, centerY + 150, 200, 60, 0x4169E1).setDepth(201);
        const buttonText = this.add.text(
            centerX,
            centerY + 150,
            'CLOSE',
            {
                fontSize: '24px',
                color: '#ffffff',
                fontFamily: 'Courier New, monospace',
                fontStyle: 'bold'
            }
        ).setOrigin(0.5).setDepth(202);
        
        buttonBg.setInteractive({ useHandCursor: true });
        buttonBg.on('pointerdown', () => {
            // Stop background music
            if (this.backgroundMusic && this.backgroundMusic.isPlaying) {
                this.backgroundMusic.stop();
            }
            // Close the modal
            if ((window as any).closeGameModal) {
                (window as any).closeGameModal();
            }
        });
        
        buttonBg.on('pointerover', () => {
            buttonBg.setFillStyle(0x6b9fff);
        });
        
        buttonBg.on('pointerout', () => {
            buttonBg.setFillStyle(0x4169E1);
        });
        
        this.winLoseOverlay = this.add.container(0, 0, [overlay, title, scoreText, timeText, buttonBg, buttonText]);
    }
    
    private _restartGame(): void {
        // Clear win/lose overlay
        if (this.winLoseOverlay) {
            this.winLoseOverlay.destroy();
            this.winLoseOverlay = undefined;
        }
        
        // Clear all moles from previous game (including frozen ones)
        this.moleManager.clearAllMoles();
        
        // Reset hat
        this.hat.resetHat();
        
        // Restart game
        this.moleManager.startGame();
    }


    update(_time: number, delta: number): void {
        // Update hat
        if (this.hat) {
            this.hat.update(delta);
            
            // Check for hat touching moles (only if hat hasn't already hit something)
            if (this.hat.isHatThrown() && !this.hat.hasHit()) {
                const hitMole = this.moleManager.checkHatCollision(
                    this.hat.getX(),
                    this.hat.getY(),
                    this.hat.getRadius()
                );
                
                if (hitMole) {
                    // Mark hat as hit to prevent multiple detections
                    this.hat.markAsHit();
                    
                    // Stop hat physics immediately
                    const hatBody = this.hat.getPhysicsBody();
                    if (hatBody) {
                        hatBody.setVelocity(0, 0);
                        hatBody.setAngularVelocity(0);
                    }
                    
                    // Catch the mole (handles blue freeze with hat or red removal)
                    this.moleManager.catchMole(hitMole, this.hat.getX(), this.hat.getY());
                    
                    // Visual feedback - hit effect
                    const hitEffect = this.add.circle(
                        this.hat.getX(),
                        this.hat.getY(),
                        30,
                        0xffff00,
                        0.8
                    ).setDepth(60);
                    
                    this.tweens.add({
                        targets: hitEffect,
                        alpha: 0,
                        scale: 2,
                        duration: 300,
                        onComplete: () => hitEffect.destroy()
                    });
                    
                    // Hide the thrown hat (mole now wears it if blue)
                    this.hat.resetHat();
                }
            }
        }
        
        // Update UI
        if (this.moleManager && this.moleManager.isGameActive()) {
            const score = this.moleManager.calculateScore();
            const totalCaught = this.moleManager.getTotalCaught();
            const targetTotal = this.moleManager.getTargetTotal();
            
            this.scoreText.setText(`SCORE: ${Math.floor(score)}`);
            this.blueMolesText.setText(
                `CAUGHT: ${totalCaught}/${targetTotal}`
            );
            this.redMolesText.setText(
                `DEM: ${this.moleManager.getBlueMolesFrozen()}  GOP: ${this.moleManager.getRedMolesCaught()}`
            );
        }
        
        // Show win/lose screen
        if (this.moleManager) {
            if (this.moleManager.isGameWon() && !this.winLoseOverlay) {
                this.showWinLoseScreen(true);
            } else if (this.moleManager.isGameLost() && !this.winLoseOverlay) {
                this.showWinLoseScreen(false);
            }
        }
    }
}

